import { DAGObject, ArticleContent, createUpvote, createDownvote, Identity } from '@amorphous/core';
import { ArticleCard } from './ArticleCard';
import type { PillMode } from './PillChoice';
import './Feed.css';

interface FeedProps {
  articles: DAGObject<ArticleContent>[];
  mode: PillMode;
  identity: Identity;
  onAddObject: (obj: DAGObject) => Promise<string>;
  getVoteCounts: (hash: string) => Promise<{ up: number; down: number; total: number }>;
  getDisplayName?: (pubkey: string) => string;
  onAuthorClick?: (pubkey: string) => void;
  onShare?: (hash: string) => void;
  blockedUsers?: Set<string>;
  friends?: Set<string>;
  onReply?: (article: DAGObject<ArticleContent>) => void;
  getParentTitle?: (hash: string) => string | undefined;
  loading?: boolean;
  peerCount?: number;
  totalObjects?: number;
  /** Callback to delete an article (only for own posts) */
  onDelete?: (hash: string) => void;
}

export function Feed({ articles, mode, identity, onAddObject, getVoteCounts, getDisplayName, onAuthorClick, onShare, blockedUsers, friends, onReply, getParentTitle, loading, peerCount = 0, totalObjects = 0, onDelete }: FeedProps) {
  const isRedPill = mode === 'red';

  // Filter out blocked users' articles
  const filteredArticles = blockedUsers && blockedUsers.size > 0
    ? articles.filter(a => !blockedUsers.has(a.creator))
    : articles;

  // Build a set of article hashes for quick lookup
  const articleHashes = new Set(filteredArticles.map(a => a.hash));

  // Separate top-level articles from replies
  const topLevelArticles = filteredArticles.filter(a =>
    !a.parents?.length || !a.parents.some(p => articleHashes.has(p))
  );

  // Group replies by parent hash
  const repliesByParent = new Map<string, DAGObject<ArticleContent>[]>();
  filteredArticles.forEach(article => {
    const parentHash = article.parents?.[0];
    if (parentHash && articleHashes.has(parentHash)) {
      const existing = repliesByParent.get(parentHash) || [];
      existing.push(article);
      repliesByParent.set(parentHash, existing);
    }
  });

  // Sort replies by timestamp (oldest first for chronological thread order)
  repliesByParent.forEach((replies, parentHash) => {
    repliesByParent.set(parentHash, replies.sort((a, b) => a.timestamp - b.timestamp));
  });

  // Sort top-level articles based on mode
  const sortedArticles = [...topLevelArticles].sort((a, b) => {
    if (isRedPill) {
      // Red pill: pure chronological (newest first)
      return b.timestamp - a.timestamp;
    } else {
      // Blue pill: friends first, then chronological
      const aIsFriend = friends?.has(a.creator) ? 1 : 0;
      const bIsFriend = friends?.has(b.creator) ? 1 : 0;
      if (aIsFriend !== bIsFriend) {
        return bIsFriend - aIsFriend; // Friends first
      }
      return b.timestamp - a.timestamp;
    }
  });

  // Recursive function to get all nested replies
  const getReplies = (hash: string): DAGObject<ArticleContent>[] => {
    const directReplies = repliesByParent.get(hash) || [];
    const allReplies: DAGObject<ArticleContent>[] = [];
    directReplies.forEach(reply => {
      allReplies.push(reply);
      allReplies.push(...getReplies(reply.hash));
    });
    return allReplies;
  };

  const handleVote = async (hash: string, value: 1 | -1) => {
    const vote = value === 1
      ? createUpvote(identity, hash)
      : createDownvote(identity, hash);
    await onAddObject(vote);
  };

  if (loading) {
    return (
      <div className={`feed feed-${mode}`}>
        <div className="feed-empty">
          <div className="loading-spinner" />
          <p>Loading feed...</p>
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    const storageStatus = totalObjects === -1
      ? 'Storage: ERROR'
      : `Objects: ${totalObjects}`;
    return (
      <div className={`feed feed-${mode}`}>
        <div className="feed-empty">
          <p>No articles yet</p>
          <p className="feed-empty-hint">
            {peerCount > 0
              ? `Peers: ${peerCount} | ${storageStatus} | Articles: 0`
              : 'Be the first to publish something!'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`feed feed-${mode}`}>
      {isRedPill && (
        <div className="feed-stats">
          <span className="stat">
            <span className="stat-value">{articles.length}</span>
            <span className="stat-label">objects</span>
          </span>
          <span className="stat">
            <span className="stat-value mono">{identity.shortId}</span>
            <span className="stat-label">identity</span>
          </span>
        </div>
      )}

      <div className="feed-articles">
        {sortedArticles.flatMap(article => {
          const replies = getReplies(article.hash);
          return [
            <ArticleCard
              key={article.hash}
              article={article}
              showHash={isRedPill}
              showSignature={isRedPill}
              showCreator={true}
              showTimestamp={true}
              cleanMode={!isRedPill}
              defaultCollapsed={!isRedPill}
              onVote={handleVote}
              getVoteCounts={getVoteCounts}
              getDisplayName={getDisplayName}
              onAuthorClick={onAuthorClick}
              onShare={onShare}
              onReply={onReply}
              getParentTitle={getParentTitle}
              isOwnPost={article.creator === identity.publicKey}
              onDelete={onDelete}
            />,
            ...replies.map(reply => (
              <ArticleCard
                key={reply.hash}
                article={reply}
                showHash={isRedPill}
                showSignature={isRedPill}
                showCreator={true}
                showTimestamp={true}
                cleanMode={!isRedPill}
                defaultCollapsed={!isRedPill}
                onVote={handleVote}
                getVoteCounts={getVoteCounts}
                getDisplayName={getDisplayName}
                onAuthorClick={onAuthorClick}
                onShare={onShare}
                onReply={onReply}
                getParentTitle={getParentTitle}
                isOwnPost={reply.creator === identity.publicKey}
                onDelete={onDelete}
              />
            ))
          ];
        })}
      </div>
    </div>
  );
}
